import React, { FormEvent } from 'react';
import InputBase from '@mui/material/InputBase';
import { Keys } from '../../../../utils/keysUtil';
import { CircularProgress } from '../../../Progress/CircularProgress';
import { SelectComponents, OptionTypeWithCode } from '../../types';
import { RichTextPresenter } from './RichTextPresenter';

export const AutocompleteInput: NonNullable<SelectComponents['Input']> = ({
  open,
  selectedOptions,
  name,
  params,
  onFocus,
  onClick,
  autoComplete,
  visibleElementsWhenSelected,
  placeholder,
  loading,
  autoFocus,
  inputProps,
  onClear
}) => {
  const notDisabledCursor = autoComplete ? 'text' : 'pointer';
  const cursor = params.disabled ? 'default' : notDisabledCursor;
  const autocompleteInProgress = open && autoComplete;
  const hasSelectedValue = selectedOptions && !Array.isArray(selectedOptions);
  const showRichText = hasSelectedValue && !autocompleteInProgress;
  const inputValue = showRichText ? '' : params.inputProps.value;

  const setInputValue = (e: React.KeyboardEvent | React.MouseEvent, value = '') => {
    if (params.inputProps.ref.current) {
      params.inputProps.ref.current.value = value;
    }
    params.inputProps.onChange({
      ...e,
      target: params.inputProps.ref.current
    } as unknown as FormEvent<HTMLInputElement | HTMLTextAreaElement>);
  };

  const selectedLabel = getLabelValue(selectedOptions);
  const tempPlaceholder = autocompleteInProgress && hasSelectedValue ? selectedLabel : placeholder;
  return (
    <div ref={params.InputProps.ref}>
      <InputBase
        {...params.InputProps}
        fullWidth={params.fullWidth}
        disabled={params.disabled}
        required={false}
        onClick={(e) => onClick?.(e)}
        inputProps={{
          ...params.inputProps,
          readOnly: !autoComplete,
          name: name,
          autoFocus: autoFocus,
          value: inputValue,
          'data-is-open': open,
          'data-autocomplete-in-progress': autocompleteInProgress,
          'data-test-selected-label': selectedLabel,
          onFocus(e) {
            onFocus(true);
            params.inputProps.onFocus?.(e);
          },
          onBlur(e) {
            onFocus(false);
            params.inputProps.onBlur?.(e);
          },
          onMouseDown(e) {
            setInputValue(e);
            params.inputProps.onMouseDown(e);
          },
          onKeyDown(e) {
            if (showRichText && !open) {
              if (Keys.is(e, 'a', 'ctrl') || Keys.is(e, 'a', 'meta')) {
                setInputValue(e, getLabelValue(selectedOptions));
                params.inputProps.onMouseDown(e as unknown as React.MouseEvent<HTMLInputElement>);
              }
              if (Keys.is(e, 'backspace')) {
                setInputValue(e);
                params.inputProps.onMouseDown(e as unknown as React.MouseEvent<HTMLInputElement>);
              }
              if (Keys.isOneOf(e, ['arrowLeft', 'arrowRight'])) {
                setInputValue(e, getLabelValue(selectedOptions));
              }
            }
          },
          style: {
            cursor,
            width: !autoComplete ? 1 : undefined,
            minWidth: !autoComplete ? 1 : undefined
          }
        }}
        style={{ cursor }}
        endAdornment={
          <>
            {loading ? <CircularProgress color="inherit" size={20} className="loading" /> : undefined}
            {inputProps?.endAdornment || params.InputProps.endAdornment}
          </>
        }
        startAdornment={
          showRichText ? (
            <RichTextPresenter
              selectedOption={selectedOptions as OptionTypeWithCode}
              visibleElementsWhenSelected={visibleElementsWhenSelected}
              dataTest="selected-value"
              variant="small"
              loading={loading}
              onClick={(e) => {
                setInputValue(e);
                params.inputProps.onMouseDown?.(e);
                onClick?.(e);
              }}
            />
          ) : (
            inputProps?.startAdornment || params.InputProps.startAdornment
          )
        }
        placeholder={showRichText ? '' : tempPlaceholder}
      />
    </div>
  );
};

function getLabelValue(selectedOption: OptionTypeWithCode | OptionTypeWithCode[] | null): string {
  if (!selectedOption || Array.isArray(selectedOption)) {
    return '';
  }
  return selectedOption.label;
}
